import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppConfiguration } from '../classes/configuration';
import { Events } from '../classes/events';
import { Profile } from '../classes/profile';
import { EVENT_WORKER_TIMER } from '../common/constants';
import { ConfigurationService } from './configuration.service';
import { EventsDeltaService } from './events-delta.service';
import { LoggerService } from './logger.service';
import { MsalConfigService } from './msal-config.service';
import { OutboundAircraftMarkersService } from './outbound-aircraft-markers.service';
import { ProcessDataService } from './process-data.service';
import { ProfileService } from './profile.service';

const useImMemoryWebApi = !environment.production && environment.inMemoryWebApiEnabled;
const worker = new Worker(new URL("../workers/event.worker", import.meta.url));

@Injectable({
  providedIn: 'root'
})
export class EventsService implements OnDestroy {
  eventData$: BehaviorSubject<Events> = new BehaviorSubject(null);
  unsubscribe$: Subject<boolean> = new Subject();
  events: Events;
  eventsURL: string;
  configuration: AppConfiguration;
  profile: Profile;
  interval: Subscription = new Subscription();

  constructor(
    private logger: LoggerService,
    private configurationService: ConfigurationService,
    private profileService: ProfileService,
    private http: HttpClient,
    private eventsDeltaService: EventsDeltaService,
    private processDataService: ProcessDataService,
    private outboundAircraftMarkersService: OutboundAircraftMarkersService,
    private msalConfigService: MsalConfigService
  ) {
    this.subscribeToConfiguration();
    this.subscribeToProfile();
   }

  subscribeToConfiguration(): void{
    this.configurationService.getConfiguration()
      .pipe(
        takeUntil(this.unsubscribe$))
        .subscribe((configuration) => {
            this.configuration = configuration;
            // Gateway direct access - no APIC logic needed
            this.eventsURL = configuration.HostApi + '/cvv/events';
      });
  }

  updateEventData(data: Events): void {
    this.eventData$.next(data);
  }

  getEventData(): Observable<Events> {
    return this.eventData$.asObservable();
  }

  driverHasVan(driverId: string): boolean {
    // Prefer delta data (refreshed after every van assignment/unassignment via getDeltas())
    // Fall back to full snapshot on initial load before any delta has arrived
    const deltaVans = this.eventsDeltaService.eventData$.getValue()?.vanEvents?.addEvents;
    const snapshotVans = this.eventData$.getValue()?.vanEvents?.addEvents;
    const vans = deltaVans ?? snapshotVans ?? [];
    return vans.some(v => v.driverId && v.driverId.toUpperCase() === driverId.toUpperCase());
  }
  
 // toggle API threading
 public startEventInterval(): void {
  this.interval = timer(0, EVENT_WORKER_TIMER).subscribe(() => this.triggerEventsUpdate());
}

  public triggerEventsUpdate(): void {
    if (useImMemoryWebApi) {
      this.runMainThread();
    } else {
      this.runWorkerThread();
    }
  }

  public stopWorkerInterval(): void {
    this.interval.unsubscribe();
  }

  public runWorkerThread(): any {
    this.logger.debug("Events Full called on worker thread", 'worker');
    // Skip this poll cycle if profile hasn't loaded yet — prevents sending driver=null.
    if (!this.profile?.adId) {
      this.logger.debug("Events Full skipped: profile/adId not yet available", 'worker');
      return;
    }
    worker.onmessage = ({ data }) => {
      if (data.error) {
        this.logger.error(`Events worker error: ${data.message} (status: ${data.status})`);
        return;
      }
      const parsedData:Events = JSON.parse(data.data);
      this.eventsDeltaService.updateLastUpdateTimestamp(parsedData.lastUpdateTimestamp);
      this.updateEventData(parsedData);
      this.processDataService.processData(parsedData, "full");
      this.logger.debug("Events Worker Finished", 'worker')
    };
    worker.onerror = (err) => {
      this.logger.error('Events worker uncaught error:', err);
    };
    this.msalConfigService.getAzureToken().subscribe((token: string) => {
      const params = new URLSearchParams();
      params.set('driver', this.profile.adId);
      worker.postMessage({
        "url": this.eventsURL + '?' + params.toString(),
        "timeout": null,
        "airplaneMarkersOnMap": JSON.stringify(this.getOutboundMarkersOnMap()),
        "authHeader": `Bearer ${token}`
      });
    });
  }

  public runMainThread(): void {
    this.logger.debug("Events Full called on main thread", 'worker');
    const noCache = new Date().getTime();
    const headers= new HttpHeaders()
      .set("Content-type", "application/json; charset=utf-8")
      .set("airplaneMarkersOnMap", JSON.stringify(this.getOutboundMarkersOnMap()));
    this.http.get<Events>('assets/data/mock-data/events-main-response1.json' + '?noCache=' + noCache, {'headers': headers})
      .subscribe((data) => {
        this.updateEventData(data);
        this.logger.debug( `GET Events response:`, data, 'http' );
      });
  }

  getOutboundMarkersOnMap(): Array<Object>{
    return this.outboundAircraftMarkersService.getOutboundMarkersOnMap();
  }

  subscribeToProfile(): void {
    this.profileService.getProfileObs().pipe(takeUntil(this.unsubscribe$))
    .subscribe((profile) => {
        if (profile) {
          this.profile = profile;
        }
      });
  }

   ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }


}

